/**
 * NOTE: this repo has no per-subtree theme mechanism. `ThemeSwitcherProvider` (src/index.tsx) is
 * global and antd-LESS based, so a second (dark) theme cannot be scoped to a single route through
 * it. The Intelligence Suite palette therefore lives in the component layer as CSS custom
 * properties declared on a wrapper element, not in LESS.
 *
 * Values come from the Figma component library (PLA-8815 design/component-inventory.md section 1),
 * except where a screen value overrides it: AF-3 was reopened on 2026-08-10 (plan.md) and the
 * screens, not the library, are now authoritative. The band type role (27/32.8, not the library's
 * 20/30) and the border/track/accent hues below are such overrides, measured off the Customer Base
 * SVG export in design/section-customer-base-svg-diff-2026-08-11.md.
 *
 * The family is `--is-font-family` below: Roboto, the design system's face (2026-10-07), loaded by
 * `./fonts`. It is scoped to this token layer rather than declared globally, because the rest of the
 * dashboard has never declared a family and is not in scope for PLA-8815. Each type role stays split
 * into size/weight/line properties so consumers never need the CSS `font` shorthand, which would
 * reset the family back to the inherited stack.
 */

import { scaledPx } from './scale';

type TokenValues = Record<string, string>;

const cssVarName = (prefix: string, token: string): string => `--is-${prefix}${token}`;

const COLORS = {
  'bg-app': '#05090F',
  'surface-section': '#101830',
  'surface-card': '#14243B',
  'surface-card-alt': '#1A2237',
  'surface-subpanel': '#141A2B',
  // Every hairline and every rail in the screens is the same #2A344D (37 of them in the Customer
  // Base export); the two roles stay separate tokens because only their values coincide today.
  'border-subtle': '#2A344D',
  'border-strong': '#2A405F',
  'track-bg': '#2A344D',
  'on-accent': '#08111F',
  'tint-positive-bg': '#15382D',
  'tint-negative-bg': '#3A1620',
  'text-primary': '#F5F7FB',
  // The screens draw the section-band title a shade cooler than every other near-white, and its
  // band description cooler than --is-text-secondary (589:32395 / 589:32396).
  'text-title': '#F5F7FF',
  'text-band-description': '#98A5BF',
  // The dominant secondary ink of the screens - 74 of the Customer Base export's text fills, against
  // 0 uses of --is-text-secondary's #AEBBD0. Held as its own role rather than folded into
  // --is-text-secondary because that would restyle every label in all nine sections at once; the
  // convergence belongs to the pending token pass (svg-diff row 23).
  'text-caption': '#98A5BF',
  'text-secondary': '#AEBBD0',
  'text-muted': '#8592AD',
  // The filter panel is drawn on its own palette: seven hues, six of which land beside an existing
  // token without matching it (#212D4B vs --is-border-subtle, #0D1428 vs --is-surface-card-alt,
  // #3E8FF0 vs --is-accent-blue, #EAF0F7 vs --is-text-primary, #8DA0BD vs --is-text-caption). They
  // are held as filter-scoped roles rather than folded into the near neighbours, because folding
  // would restyle all nine sections to fix one panel; the convergence belongs to the pending token
  // pass (AF-3). Measured off the eight card exports, design/filter-panel-spec-2026-08-11.md § 3.
  'filter-border': '#212D4B',
  'filter-surface': '#0D1428',
  'filter-accent': '#3E8FF0',
  'filter-text': '#EAF0F7',
  'filter-text-muted': '#8DA0BD',
  'filter-radio-border': '#767676',
  // The chip caret is its own grey, neither ink nor accent (filter-spec § 1). A `filter-divider`
  // token sat here too, at a pixel-sampled #1C2742; the scene graph shows the card has no divider
  // layer at all — it is `div.panel`'s own border-top in --is-filter-border — so it is gone.
  'filter-caret': '#7D8FAB',
  'accent-blue': '#3D8BFF',
  'accent-cyan': '#20B8CD',
  'accent-purple': '#6D5DFC',
  'accent-teal': '#0AA89A',
  // The series hue for a rank or band that names no category of its own ("Other", "One product").
  'accent-neutral': '#94A0B7',
  // Dormant's own grey, Figma `color/Gray/Manatee 49`, read twice on the segment (589:32414 /
  // 589:29695) and the legend dot (589:32349) and in both frames. NOT --is-accent-neutral: that
  // #94A0B7 is already the `No holding` holdings-bar fill on the same card, so reusing it would
  // collide two distinct series roles onto one value.
  'accent-slate': '#7F8AA3',
  // PLA-8815: the "unknown / other" hue on the Payments band, read on both the intent bar's last
  // segment (589-28868) and the rail mix's last row (589-28912). It is a fourth distinct grey —
  // neither --is-accent-neutral #94A0B7, --is-accent-slate #7F8AA3 nor --is-text-muted #8592AD — so
  // it is recorded rather than folded into the nearest one.
  'accent-unknown': '#8994AA',
  // PLA-8815: the neutral band of the sentiment split, Figma `color/azure/60 2` "Bali Hai", read on
  // the middle segment of Experience & Value's distribution bar (589:27922). A FIFTH distinct grey
  // — not --is-accent-neutral #94A0B7, --is-accent-slate #7F8AA3, --is-accent-unknown #8994AA nor
  // --is-text-muted #8592AD. The band had been painted --is-warning amber, which the file does not
  // draw anywhere on this bar.
  'sentiment-neutral': '#8793AA',
  // The whybox heading ink. A near-white of its own, brighter than --is-text-primary's usage here
  // and not otherwise in the palette (589:32325 / 589:32329).
  'whybox-heading': '#E2E7F0',
  // PLA-8815: the resting bar of a tile trend, sampled on the seven-bar charts of BOTH growth cards
  // at 100% — `589-29844` (Opportunity Signal Count / Customers with Active Signals) and `589-29936`
  // (Growth Planner Handoff Eligible / Not Eligible / Blocked) — where six of the seven bars read a
  // flat #D7DAF3 and the seventh reads --is-positive. A pale lavender that is in no other role here:
  // not --is-text-primary #F5F7FB, not --is-whybox-heading #E2E7F0, and a long way from the
  // --is-accent-blue #3D8BFF the bars had been painted. The highlighted bar takes --is-positive, so
  // this token is only ever the UNselected reading.
  'trend-bar': '#D7DAF3',
  // PLA-8815: the outline of a step in the handoff FLOW rail (`589-29991`, `div.flow` 628 x 47),
  // read on a mid-height scanline at y=831 where the five non-terminal chips give a clean #E2E7F0 at
  // x=963 and x=990 while the sixth reads #16544E / #187C62 / #1CA576, which is --is-positive
  // antialiased. Same value as --is-whybox-heading, held apart from it for the reason
  // --is-accent-slate is held apart from --is-accent-neutral: that one is a heading INK and this is a
  // stroke, and folding them would move every whybox heading to restyle a rail.
  'flow-node-border': '#E2E7F0',
  positive: '#1EB980',
  negative: '#EE6475',
  warning: '#F5A623',
  // PLA-8815: the Growth Planner slide-out's handoff-check table. All three are the file's own and
  // all three differ from the tokens above, so they are held apart rather than folded in: the table
  // rule and its four dividers read #E4E8F0 on 589:33522 and 589:33523/33526/33529/33532 (a LIGHT
  // rule on the dark panel -- confirmed on the rendered canvas at 276%, not inferred from the
  // panel); the cleared verdict reads #159F73 against --is-positive #1EB980; and the unmet one
  // #D97706 against --is-warning #F5A623.
  'handoff-check-rule': '#E4E8F0',
  'handoff-check-passed': '#159F73',
  'handoff-check-warning': '#D97706',
  // PERX3-REDESIGN token pass: the roles below close AF-3, the literals that used to sit in the
  // section and component files. Dark values are the literals verbatim, so the flag-OFF render is
  // unchanged; COLORS_LIGHT gives each one its Perx-3 reading.
  'chip-surface': '#13284D',
  'field-label': '#64709A',
  'accent-indigo': '#4F46E5',
  'tint-accent-bg': '#1B1953',
  'badge-positive-border': '#91D5AD',
  'badge-warning-border': '#A77A2C',
  'badge-positive-ink': '#16834F',
  'panel-outline': '#FFFFFF',
  'text-strong': '#FFFFFF',
  'control-surface': '#FFFFFF',
  'sentiment-positive': '#59D9A0',
  'severity-high': '#E98080',
  'severity-medium': '#F3B95F',
  'holding-gradient-top': '#12213D',
  'gradient-top-purple': '#8B7EFE',
  'gradient-top-blue': '#6EA8FF',
  'gradient-top-cyan': '#5BC8D7',
  'gradient-top-warning': '#FFC76C',
  'gradient-top-positive': '#53E5AF',
  'gradient-top-negative': '#FB8C9A',
  'chart-line-purple': '#B09FFF',
  'chart-fill-purple': '#B09FFF',
  'chart-line-warning': '#FFC25C',
  'chart-fill-warning': '#FFE49F',
  'chart-line-negative': '#FF95A2',
  'chart-fill-negative': '#FF9F9F',
  'chart-line-positive': '#5ADCAC',
  'chart-fill-positive': '#CDFF9F',
  // PERX3-REDESIGN: the two literals that survived the AF-3 token pass because neither is a flat
  // colour. `dot-track` is the dotted rail of the reversal band, Layer 1's measurement of
  // `589:28399` verbatim; `warning-rgb` is `warning`'s own channel triplet, held as raw channels
  // because its two consumers compose it at 10% and 35% alpha and `rgba()` cannot take a hex var.
  'dot-track': 'rgba(69, 68, 89, 0.4)',
  'warning-rgb': '245, 166, 35',
  // The filter panel's selected row, `FilterCard`'s last literal: #3E8FF0 at 18%, i.e. the panel's
  // own --is-filter-accent rather than --is-accent-blue. Held as a token because the alpha makes it
  // a value neither `filter-accent` nor `chip-surface` can express.
  'filter-selected-row-tint': 'rgba(62, 143, 240, 0.18)',
  // PERX3-REDESIGN round 3: the header's industry line becomes a pill under the flag. The dark
  // readings are what the line already draws - no fill at all, and `positive`'s own #1EB980 - so
  // the flag-OFF header is byte-identical while the light variant can paint a neutral chip.
  'industry-pill-bg': 'transparent',
  'industry-pill-ink': '#1EB980',
  // PERX3-REDESIGN round 8: the pill's edge. Dark draws none - the dark pill has no fill either, so
  // an edge would be the only thing marking a line that reads as text there.
  'industry-pill-border': 'transparent',
  // PERX3-REDESIGN round 4: the solid primary button. The dark readings are the fill the screens
  // draw (`#4F46E5`, the value `GrowthPlannerCta` and the Growth Planner footer already carry) and
  // white on it, but NOTHING reads these keys with the flag OFF - each consumer keeps its own
  // `background`/`color` and layers this pair only under `$perx3`, because the four solid CTAs
  // disagree about their OFF ink (`text-primary`, `filter-text`, `onAction`) and one shared token
  // could not reproduce all three. `button-hover-bg` is the only state the redesign adds.
  'button-bg': '#4F46E5',
  'button-ink': '#FFFFFF',
  'button-hover-bg': '#4F46E5'
} as const;

/**
 * PERX3-REDESIGN: the light reading of the SAME key set, derived from the dashboard the rest of the
 * app already ships - `@primary-color` #2665ee, the sider navy #202c46 and antd's grey and status
 * ramps - rather than from a new palette. The accents are the desaturated readings of those status
 * hues, and the dark palette's gradients collapse to two close tints, so nothing neon survives.
 *
 * The `Record<keyof typeof COLORS, string>` annotation is the contract: a key added to COLORS
 * without a light reading fails the build rather than resolving to an empty custom property.
 */
const COLORS_LIGHT: Record<keyof typeof COLORS, string> = {
  'bg-app': '#f0f2f5',
  'surface-section': '#ffffff',
  'surface-card': '#ffffff',
  'surface-card-alt': '#fafafa',
  'surface-subpanel': '#fafafa',
  'border-subtle': '#f0f0f0',
  'border-strong': '#d9d9d9',
  'track-bg': '#f0f0f0',
  'on-accent': '#ffffff',
  'tint-positive-bg': '#f6ffed',
  'tint-negative-bg': '#fff1f0',
  'text-primary': '#262626',
  'text-title': '#202c46',
  // WCAG AA on the light cards is what picks these three, not the sider palette: #989eaa reads
  // 2.7:1 on #ffffff and #8c8c8c 3.4:1, both under the 4.5:1 floor for text. #6b7280 is 4.8:1 and
  // #595959 (antd grey-8, already the body ink below) is 7.0:1 / 6.7:1 on #fafafa.
  'text-band-description': '#6b7280',
  'text-caption': '#595959',
  // #989eaa is the app's secondary ink on the sider's navy; on these white cards it reads 2.5:1, so
  // the body role takes antd's #595959 and #989eaa stays on the band description only.
  'text-secondary': '#595959',
  'text-muted': '#595959',
  'filter-border': '#d9d9d9',
  'filter-surface': '#ffffff',
  'filter-accent': '#2665ee',
  'filter-text': '#262626',
  'filter-text-muted': '#595959',
  'filter-radio-border': '#d9d9d9',
  'filter-caret': '#8c8c8c',
  // The six accents are the chart series palette, anchored on @primary-color and desaturated from
  // there; consumers index them by name, so the series order is the order of these six keys.
  'accent-blue': '#2665ee',
  'accent-cyan': '#2f9ab0',
  'accent-purple': '#7265c9',
  'accent-teal': '#3aa08f',
  'accent-neutral': '#b0b7c3',
  'accent-slate': '#98a1b3',
  'accent-unknown': '#c0c6d1',
  'sentiment-neutral': '#a8b0bf',
  'whybox-heading': '#202c46',
  'trend-bar': '#dde3f5',
  'flow-node-border': '#d9d9d9',
  // The three status inks are the antd ramp taken two steps past its -6 reading, because -6 and -7
  // both fail AA as text on #ffffff: green-6 #52c41a 2.2:1, green-7 #389e0d 3.5:1, gold-7 #d48806
  // 2.9:1, gold-8 #ad6800 4.4:1. green-8 #237804 is 5.6:1 (5.4:1 on the #f6ffed badge fill) and
  // gold-9 #874d00 is 6.8:1. red-7 #cf1322 already clears it at 5.6:1 and is left alone. The chart
  // line/fill keys below keep their -6/-7 readings: those are graphics, not text.
  positive: '#237804',
  negative: '#cf1322',
  warning: '#874d00',
  'handoff-check-rule': '#f0f0f0',
  'handoff-check-passed': '#237804',
  'handoff-check-warning': '#874d00',
  'chip-surface': '#e8effd',
  'field-label': '#595959',
  'accent-indigo': '#2665ee',
  'tint-accent-bg': '#e8effd',
  'badge-positive-border': '#b7eb8f',
  'badge-warning-border': '#ffd591',
  'badge-positive-ink': '#237804',
  'panel-outline': '#d9d9d9',
  'text-strong': '#202c46',
  'control-surface': '#ffffff',
  // Read as INK, not as a fill: `CustomerExperienceSection`'s SEVERITY_INK / SCORE_TILE_INKS put
  // all three on text, so they take the same AA-clearing readings as the status trio above.
  'sentiment-positive': '#237804',
  'severity-high': '#cf1322',
  'severity-medium': '#874d00',
  // Flat against --is-filter-surface: the dark palette's vertical fade has no light equivalent that
  // is not banding on a white card.
  'holding-gradient-top': '#ffffff',
  'gradient-top-purple': '#8d82d9',
  'gradient-top-blue': '#5b8df2',
  'gradient-top-cyan': '#58aabd',
  'gradient-top-warning': '#e9a83a',
  'gradient-top-positive': '#5ab534',
  'gradient-top-negative': '#e05263',
  'chart-line-purple': '#7265c9',
  'chart-fill-purple': '#e5e2f5',
  'chart-line-warning': '#d48806',
  'chart-fill-warning': '#fff7e6',
  'chart-line-negative': '#cf1322',
  'chart-fill-negative': '#fff1f0',
  'chart-line-positive': '#389e0d',
  'chart-fill-positive': '#f6ffed',
  // A neutral at 15% rather than the dark palette's slate: #454459 at 40% reads as a bruise on a
  // white rail. Decoration, so it carries no contrast floor.
  'dot-track': 'rgba(0, 0, 0, 0.15)',
  'warning-rgb': '135, 77, 0',
  // @primary-color at 12%, not 18%: the same alpha over a white row reads as a saturated fill rather
  // than a selection tint, and the row's ink is --is-filter-text #262626, which needs the row to
  // stay near-white to keep its 15.9:1.
  'filter-selected-row-tint': 'rgba(38, 101, 238, 0.12)',
  // The antd layout grey the rest of the app uses for a resting chip, with the sider ink on it:
  // #202c46 on #f0f2f5 is 12.4:1, so the 12px pill text clears AA with room.
  // Round 8 (user): #f0f2f5 IS the page ground, so the pill read as no background at all. White
  // with the app's own border grey around it is what separates it: #202c46 on #ffffff is 14.4:1,
  // and the #d9d9d9 edge is antd's resting border, the same one every card on the page carries.
  'industry-pill-bg': '#ffffff',
  'industry-pill-ink': '#202c46',
  'industry-pill-border': '#d9d9d9',
  // Round 4 (user): every solid primary button is black, not blue. #FFFFFF on #262626 is 15.9:1 and
  // on the #000000 hover 21:1, so both states clear AA with room at the 11px label the CTAs carry.
  'button-bg': '#262626',
  'button-ink': '#FFFFFF',
  'button-hover-bg': '#000000'
};

// PLA-8844 (design system 2026-10-07): Roboto, loaded by `./fonts`. The fallback is antd's own default
// stack verbatim (antd/lib/style/themes/default.less:83), so a failed or blocked webfont degrades to
// what this screen rendered before a webfont shipped rather than to an unstyled serif.
const FONT = {
  'font-family':
    "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
} as const;

/**
 * PERX3-REDESIGN: under the flag the suite inherits the app's own face, so the VARIABLE resolves to
 * `inherit` rather than the property being dropped at each consumer. That matters for the three
 * portalled scopes, which declare the token layer on an element outside the route: leaving Roboto in
 * the variable would have reinstated it through `${tokens.font['font-family']}` there even though
 * `IntelligenceContentScope` no longer emits the rule. `./fonts` still ships its @font-face blocks
 * under the flag, but a browser only fetches a webfont for a glyph that asks for the family, and
 * with this reading nothing under the flag does — so ON downloads zero font bytes.
 */
const FONT_LIGHT: Record<keyof typeof FONT, string> = {
  'font-family': 'inherit'
};

/**
 * PLA-8844: the design system's nine-step type scale (2026-10-07). Every role below reads one step;
 * line height is 140% of the size and tracking is 0 throughout. Roboto ships 400/500/700 here, so a
 * role drawn heavier or lighter than those takes the nearest of them.
 */
export const TYPE_SCALE = {
  heading1: 28,
  heading2: 24,
  heading3: 20,
  heading4: 18,
  heading5: 16,
  body1: 14,
  body2: 13,
  body3: 12,
  caption: 11
} as const;

export type TypeStep = keyof typeof TYPE_SCALE;

export const TYPE_LINE_HEIGHT = 1.4;

const px = (value: number): string => `${Math.round(value * 100) / 100}px`;

const stepSize = (step: TypeStep): string => px(TYPE_SCALE[step]);

const stepLine = (step: TypeStep): string => px(TYPE_SCALE[step] * TYPE_LINE_HEIGHT);

const role = (name: string, step: TypeStep, weight?: string): TokenValues => ({
  [`${name}-size`]: stepSize(step),
  ...(weight ? { [`${name}-weight`]: weight } : {}),
  [`${name}-line`]: stepLine(step)
});

const STEPS: TokenValues = Object.keys(TYPE_SCALE).reduce<TokenValues>(
  (acc, step) => ({ ...acc, ...role(step, step as TypeStep) }),
  {}
);

const TYPE: TokenValues = {
  ...STEPS,
  // Page title, page subtitle, section title and section subtitle.
  ...role('display', 'heading1', '400'),
  ...role('page-subtitle', 'body1', '700'),
  ...role('section-title', 'heading2', '700'),
  ...role('band-description', 'body3', '400'),
  // Metrics section header: the title of every card on both pages.
  ...role('card-header', 'heading4', '700'),
  // Container title (sub-blocks inside a card) and body.
  ...role('card-title', 'body2', '700'),
  ...role('card-description', 'body3', '400'),
  // Metric numbers and their growth/decline line.
  ...role('stat', 'heading4', '700'),
  'stat-lead-size': stepSize('heading4'),
  // Metrics title.
  ...role('metric-title', 'body3', '500'),
  ...role('metric-delta', 'body3', '500'),
  // Data body.
  ...role('body', 'body3', '400'),
  ...role('block-heading', 'caption', '700'),
  'block-heading-tracking': '0px',
  ...role('whybox-heading', 'caption', '700'),
  // Data section title.
  ...role('detail-group-title', 'body2', '700'),
  ...role('confidence-badge', 'body3', '700'),
  // Helping text.
  ...role('label', 'caption', '400'),
  ...role('score-dial-value', 'body2', '700'),
  'score-dial-value-min-size': stepSize('caption'),
  ...role('flow-step', 'caption', '400'),
  ...role('filter-annotation', 'caption', '400'),
  ...role('filter-default-note', 'caption', '400'),
  ...role('handoff-step', 'caption', '700'),
  'handoff-check-size': stepSize('body3'),
  'handoff-check-line': stepLine('body3'),
  'handoff-check-label-weight': '400',
  'handoff-check-value-weight': '700'
};

const SPACE = {
  '4': '4px',
  '8': '8px',
  '12': '12px',
  '16': '16px',
  '20': '20px',
  '24': '24px',
  '32': '32px',
  // PLA-8815: `2570:4672` and `2561:4246` draw `section.grid` at 40px between their two cards — the
  // widest gap the Intelligence Suite uses, and the first reading past the 32 this scale stopped at.
  '40': '40px'
} as const;

const RADIUS = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  // Keyed by its value rather than a t-shirt size, mirroring Figma's own `radius/13` variable: it
  // sits between `md` and `lg` and is a distinct variable in the file, not a step anyone could infer
  // from the scale. Drives the Data Trust tiles and the filter-card shell.
  '13': '13px',
  lg: '18px',
  // PLA-8815, keyed by value for the same reason as `13`: the Growth Planner slide-out's primary
  // action reads a flat 10px (589:33543) and its receipt box carries the file's own `corner
  // radius/10` variable (615:2756). It sits between `sm` and `md` and neither rounds to it.
  '10': '10px',
  // PLA-8815, keyed by value for the same reason as `10` and `13`: the handoff flow rail's step
  // carries the file's own `corner radius/9` variable, read off `589-29992`'s Properties panel. A
  // third distinct value between `sm` and `md`, and the rail had been drawn at `10`, which is the
  // slide-out's radius rather than this one.
  '9': '9px',
  // PLA-8815, keyed by value for the same reason as `9`, `10` and `13`: the Growth Planner
  // slide-out's own shell carries the file's `corner radius/16` variable, read off `589:33479`'s
  // Properties panel (520x900, stroke weight/1, #101830). A fourth distinct value, and the nearest
  // step `lg` is 18px, which is not what the shell is drawn at.
  '16': '16px',
  pill: '999px'
} as const;

// Figma draws the Data Trust tiles, the source chips and the alert rows with a 0.8px stroke — one
// recurring weight rather than three roundings of 1px, so it is a variable of its own. Sub-pixel and
// deliberately so: the browser snaps it to whole device pixels at dpr 1, which is why it reads back
// as 1px in Chromium and 0.5px in WebKit and can only be verified from the declared rule.
const STROKE = {
  hairline: '0.8px'
} as const;

const ELEVATION = {
  cta: '0 4px 14px rgba(109, 93, 252, 0.35)',
  overlay: '0 10px 34px rgba(0, 0, 0, 0.5)',
  pointer: '0 4px 4px rgba(0, 0, 0, 0.25)',
  // Figma records this focus ring as a zero-blur, zero-offset shadow with no spread and 16% alpha:
  // invisible geometry AND, composited over --is-bg-app, a 1.19:1 contrast. 3px is the smallest
  // spread that renders the ring the mockups show, and 0.9 alpha clears the 3:1 WCAG 2.1 SC 1.4.11
  // minimum against both --is-bg-app and --is-surface-card.
  'focus-ring': '0 0 0 3px rgba(62, 143, 240, 0.9)',
  // The filter card's value box carries this in the scene graph as a sibling shadow layer
  // (`621:3810`): x 0, y 0, blur 0, spread 3, #3E8FF0 at 16%. Held at Figma's alpha rather than
  // folded into --is-elev-focus-ring, which is the same recipe deliberately raised to 0.9 to clear
  // WCAG — this one is decoration on a non-focusable readout, so it has no contrast floor to meet.
  'filter-value-glow': '0 0 0 3px rgba(62, 143, 240, 0.16)'
} as const;

/**
 * PERX3-REDESIGN: the three accent-tinted elevations are the dark palette's own hues — #6D5DFC for
 * the CTA glow and #3E8FF0 for the two rings — so sharing them with the light variant painted a
 * purple halo under every primary chart action and a cyan-blue ring on a white card. Each takes the
 * light palette's anchor, @primary-color #2665ee (38, 101, 238), at the SAME alpha and geometry:
 * the reading changes hue, not depth.
 *
 * `overlay` and `pointer` are unchanged: both are black at their own alpha, which is what a drop
 * shadow is in either reading, and neither carries an accent.
 */
const ELEVATION_LIGHT: Record<keyof typeof ELEVATION, string> = {
  cta: '0 4px 14px rgba(38, 101, 238, 0.35)',
  overlay: ELEVATION.overlay,
  pointer: ELEVATION.pointer,
  // Composited at 0.9 the ring reads 4.2:1 on #ffffff and 3.8:1 on --is-bg-app #f0f2f5, so it keeps
  // clearing the 3:1 WCAG 2.1 SC 1.4.11 minimum the dark reading was raised to 0.9 for.
  'focus-ring': '0 0 0 3px rgba(38, 101, 238, 0.9)',
  'filter-value-glow': '0 0 0 3px rgba(38, 101, 238, 0.16)'
};

const toVarRefs = <T extends TokenValues>(prefix: string, values: T): Record<keyof T, string> => {
  const refs = {} as Record<keyof T, string>;

  Object.keys(values).forEach(token => {
    refs[token as keyof T] = `var(${cssVarName(prefix, token)})`;
  });

  return refs;
};

/**
 * A bare px length - the only value shape the responsive scale applies to. Weights, colours, the
 * font stack and the elevation shadows (whose px live inside a multi-part shadow string) all fall
 * outside it, so a scaled group can be passed wholesale without listing which of its tokens are
 * lengths.
 */
const PX_VALUE = /^-?\d*\.?\d+px$/;

const declarationsFor = (prefix: string, values: TokenValues, scale = false): string[] =>
  Object.keys(values).map(token => {
    const value = values[token];
    const emitted = scale && PX_VALUE.test(value) ? scaledPx(parseFloat(value)) : value;

    return `${cssVarName(prefix, token)}: ${emitted};`;
  });

/**
 * The Roboto stack as a literal, for surfaces that render OUTSIDE `IntelligenceThemeScope` and so
 * cannot resolve `tokens.font['font-family']` (which is a `var(--is-font-family)` reference).
 */
export const INTELLIGENCE_FONT_FAMILY: string = FONT['font-family'];

export const tokens = {
  color: toVarRefs('', COLORS),
  font: toVarRefs('', FONT),
  type: toVarRefs('type-', TYPE),
  space: toVarRefs('space-', SPACE),
  radius: toVarRefs('radius-', RADIUS),
  stroke: toVarRefs('stroke-', STROKE),
  elevation: toVarRefs('elev-', ELEVATION)
};

/**
 * PERX3-REDESIGN round 5: the single-hue tonal series ramp, declared in the LIGHT reading ONLY.
 *
 * Every chart in the suite picked its own hues — a blue here, a purple and an amber there — so two
 * ordered categories on one screen read as two unrelated legends. Under the flag they all draw from
 * these eight stops instead, and ORDER carries the meaning: an ordered category maps in order, the
 * healthiest or most active band on stop 1 and the churned or lowest one on the palest stop. Stops
 * 7 and 8 are the two neutrals, for a bucket that names no position on the scale.
 *
 * Declared on one variant and not the other ON PURPOSE. A consumer asks for a stop as
 * `var(--is-series-3, <the hue it already drew>)`, so with the flag OFF the custom property does
 * not exist, the fallback is what resolves, and the render is the one that shipped — without the
 * ~130 extra custom properties a per-palette token family would have put on every scope element,
 * and without a runtime branch in ~30 consumers.
 */
const SERIES_LIGHT = {
  'series-1': '#202c46',
  'series-2': '#2665ee',
  'series-3': '#5b8af3',
  'series-4': '#8fb0f7',
  'series-5': '#bdd0fa',
  'series-6': '#dbe6fc',
  'series-7': '#4b5a7a',
  'series-8': '#8c8c8c',
  // The label ink a FILLED stop carries. The ramp crosses the contrast boundary between stop 3 and
  // stop 4: #FFFFFF is 3.3:1 on #5b8af3 and 1.9:1 on #8fb0f7, while #262626 is 4.0:1 on #5b8af3 and
  // 7.5:1 on #8fb0f7. So stops 1-3 take the light ink and 4-8 the dark one.
  'on-series-1': '#FFFFFF',
  'on-series-2': '#FFFFFF',
  'on-series-3': '#FFFFFF',
  'on-series-4': '#262626',
  'on-series-5': '#262626',
  'on-series-6': '#262626',
  'on-series-7': '#FFFFFF',
  'on-series-8': '#262626'
} as const;

export type IntelligenceThemeVariant = 'dark' | 'light';

/**
 * Every non-colour token is shared: the Perx 3 reading changes the palette, not the type scale,
 * spacing, radii or elevation.
 */
export const intelligenceTokenDeclarationsFor = (variant: IntelligenceThemeVariant): string =>
  [
    ...declarationsFor('', variant === 'light' ? COLORS_LIGHT : COLORS),
    ...declarationsFor('', variant === 'light' ? FONT_LIGHT : FONT),
    // PLA-8844: the four length-bearing groups follow `--is-scale`. Colours, the font stack and the
    // elevation shadows are passed unscaled.
    ...declarationsFor('type-', TYPE, true),
    ...declarationsFor('space-', SPACE, true),
    ...declarationsFor('radius-', RADIUS, true),
    ...declarationsFor('stroke-', STROKE, true),
    ...declarationsFor('elev-', variant === 'light' ? ELEVATION_LIGHT : ELEVATION),
    // Light only: see SERIES_LIGHT. With the flag off these eight are undeclared and every consumer
    // falls back to the hue it already drew.
    ...(variant === 'light' ? declarationsFor('', SERIES_LIGHT) : [])
  ].join('\n  ');

/** The dark reading, kept as a named export so flag-OFF consumers and their tests are untouched. */
export const intelligenceTokenDeclarations: string = intelligenceTokenDeclarationsFor('dark');

/**
 * What a PORTALLED scope declares - `BreakdownModal`, `GrowthPlannerSlider`, `OrbitAnswerFooter`.
 * Each mounts outside `IntelligenceContentScope`'s subtree, so it re-declares the token layer on its
 * own root; before this helper all three re-declared the DARK one unconditionally and pinned Inter,
 * which is how a light page opened a dark overlay.
 *
 * The family rule is gated exactly as `IntelligenceContentScope` gates it (`$ownFontFamily`): the
 * suite owns the face only with the flag OFF, and the OFF string is byte-identical to what the three
 * consumers declared before.
 */
export const intelligenceScopeDeclarations = (isPerx3: boolean): string =>
  isPerx3
    ? intelligenceTokenDeclarationsFor('light')
    : `${intelligenceTokenDeclarationsFor('dark')}\n\n  font-family: ${
        tokens.font['font-family']
      };`;
